/**
 * Utility functions for network error detection and handling
 */

/**
 * Checks if an error is a network-related error
 * Handles both Firestore errors and fetch API errors
 */
export function isNetworkError(error: any): boolean {
  if (!error) return false;

  // Check if navigator.onLine is available and indicates offline
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return true;
  }

  const errorMessage = (error.message || '').toLowerCase();
  const errorCode = (error.code || '').toLowerCase();
  const errorName = (error.name || '').toLowerCase();

  // Common network error patterns
  const networkErrorPatterns = [
    'failed to fetch',
    'networkerror',
    'network request failed',
    'networkerror when attempting to fetch resource',
    'load failed',
    'network connection',
    'internet connection',
    'no internet',
    'offline',
    'connection refused',
    'connection reset',
    'connection timed out',
    'timeout',
    'econnrefused',
    'enotfound',
    'eai_again',
  ];

  // Firestore specific error codes
  const firestoreNetworkCodes = [
    'unavailable',
    'deadline-exceeded',
    'internal',
    'aborted',
  ];

  // Check error message
  if (networkErrorPatterns.some(pattern => errorMessage.includes(pattern))) {
    return true;
  }

  // Check error code (Firestore uses codes like 'unavailable')
  if (firestoreNetworkCodes.includes(errorCode)) {
    return true;
  }

  // Check error name
  if (errorName === 'networkerror' || errorName === 'typeerror') {
    // TypeError often occurs with network failures
    if (errorMessage.includes('fetch') || errorMessage.includes('network')) {
      return true;
    }
  }

  // Check for specific Firestore error structure
  if (error.code === 'unavailable' || error.code === 'deadline-exceeded') {
    return true;
  }

  return false;
}

/**
 * Gets a user-friendly error message for network errors
 */
export function getNetworkErrorMessage(error?: any): string {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.';
  }

  if (isNetworkError(error)) {
    return 'אין חיבור לאינטרנט. אנא בדוק את החיבור שלך ונסה שוב.';
  }

  return 'שגיאה בטעינת הנתונים. אנא נסה שוב מאוחר יותר.';
}
